<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { CodeType } from '@/gql/graphql';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { ORGANIZATION_USER_ROLES } from '@/constants';
import { openRouterLink } from '@/utils/router-utils';

const router = useRouter();

// --- Component properties ---
const props = defineProps<{
  codes: CodeType[];
  loading: boolean;
}>();

const noCodes = computed(() => {
  return props.codes.length === 0;
});

// --- Code Table Fields ---
const codeTableFields = [
  {
    key: 'code',
    label: 'Code',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'items',
    label: 'Associated Items',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'organization',
    label: 'Organization',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'orderRef',
    label: 'Order',
    sortable: true,
    tdClass: 'align-middle',
  },
  {
    key: 'actions',
    label: '',
    sortable: false,
    tdClass: 'align-middle text-end min-width',
  },
];

function getOrganizationDetails(code: CodeType) {
  // check organization association
  if (!code.organization) {
    return '-';
  }
  // identify organization owner
  const owner = code.organization.usersAssociated?.find(
    (assoc) =>
      assoc &&
      assoc.userRole === ORGANIZATION_USER_ROLES.OWNER &&
      assoc.removedAt === null
  );
  // return org name + owner email
  return code.organization.name + (owner ? ` (${owner.user?.email})` : '');
}

// --- Copy Code Functionality ---
const copied = ref<string | null>(null);
function copyCode(code: string) {
  navigator.clipboard.writeText(code).then(() => {
    copied.value = code;
    setTimeout(() => (copied.value = null), 2_000);
  });
}

// --- Code Detail Request ---
function handleCodeDetailRequest(
  code: CodeType,
  _index: number,
  evt: MouseEvent
) {
  // Any element that lives inside the “no-row-click” cell?  Abort.
  if (
    evt.target instanceof HTMLElement &&
    evt.target.closest('.no-row-click')
  ) {
    return;
  }
  // check if code is available
  if (!code || !code.code) {
    console.error('Code detail request failed, code is missing');
    return;
  }
  // identify the target
  openRouterLink(
    {
      name: 'CodeDetail',
      params: { code: code.code },
    },
    router,
    evt
  );
}
</script>
<template>
  <div>
    <b-alert v-model="noCodes" variant="secondary"
      >No codes associated with kits or coupons currently available.</b-alert
    >
    <b-card v-if="props.codes.length > 0" class="my-3">
      <b-table
        :items="props.codes"
        :busy="loading"
        :fields="codeTableFields"
        small
        striped
        responsive
        hover
        class="browser-table"
        @row-clicked="handleCodeDetailRequest"
      >
        <!-- Default data cell scoped slot -->
        <template #cell(code)="data">
          <span
            :id="`code-target-${data.value}`"
            class="no-row-click"
            @click.stop="copyCode(data.value)"
          >
            <strong>{{ data.value }}</strong>
          </span>
          <BTooltip :target="`code-target-${data.value}`">
            <span v-if="copied === data.value">
              Code {{ data.value }} copied to clipboard
              <font-awesome-icon :icon="['fas', 'check']" />
            </span>
            <span v-else>
              Click to copy code to clipboard
              <font-awesome-icon :icon="['fas', 'copy']" />
            </span>
          </BTooltip>
        </template>
        <!-- Kits and coupons -->
        <template #cell(items)="data">
          <!-- kit badge -->
          <template v-if="data.item.kit">
            <BBadge
              class="mx-1"
              variant="primary"
              :id="`kit-target-${data.item.kit.id}`"
            >
              <font-awesome-icon :icon="['fas', 'cube']" />
              {{ data.item.kit.name }}
            </BBadge>
            <BTooltip :target="`kit-target-${data.item.kit.id}`">
              {{ data.item.kit.description }}
              <hr />
              <strong>Version:</strong>
              {{
                data.item.kit.version ? data.item.kit.version : 'not specified'
              }}<br />
              <strong>Barcodes whitelist:</strong>
              {{ data.item.kit.barcode }}<br />
              <strong>UMI length:</strong>
              {{ data.item.kit.umiLength }}<br />
              <strong>Samples:</strong>
              {{ data.item.kit.sampleCount }}
            </BTooltip>
          </template>
          <!-- license badge -->
          <template v-if="data.item.license">
            <BBadge class="mx-1" variant="danger">
              <font-awesome-icon :icon="['fas', 'certificate']" />
              {{ data.item.license.name }}
            </BBadge>
          </template>
          <!-- list coupons as badges -->
          <template v-for="coupon in data.item.coupons" :key="coupon.id">
            <BBadge
              :id="`coupon-target-${coupon.id}`"
              :variant="
                coupon.couponType.includes('compute') ? 'warning' : 'success'
              "
              class="mx-1"
            >
              <font-awesome-icon :icon="['fas', 'ticket']" />
              {{ coupon.couponType }}
            </BBadge>
            <BTooltip :target="`coupon-target-${coupon.id}`">
              {{ coupon.unitsRemaining }} of {{ coupon.unitsTotal }}
              {{ coupon.units }} remaining
            </BTooltip>
          </template>
        </template>
        <!-- Oragniztion details -->
        <template #cell(organization)="data">
          {{ getOrganizationDetails(data.item) }}
        </template>
        <!-- Code actions -->
        <template #cell(actions)="data">
          <BButtonGroup>
            <b-button
              variant="outline-secondary"
              class="btn-sm"
              v-if="data.item.comment"
            >
              <font-awesome-icon
                :icon="['fa', 'comment']"
                :id="`comment-target-${data.item.id}`"
              />
            </b-button>
            <b-button
              variant="outline-secondary"
              class="btn-sm"
              @click="handleCodeDetailRequest(data.item, data.item.id, $event)"
            >
              <font-awesome-icon :icon="['fa', 'eye']" />
            </b-button>
          </BButtonGroup>
          <BTooltip
            v-if="data.item.comment"
            :target="`comment-target-${data.item.id}`"
            triggers="hover"
          >
            {{ data.item.comment }}
          </BTooltip>
        </template>
      </b-table>
    </b-card>
  </div>
</template>
